<template>
  <div class="topbar-item" v-if="currentUser.data">
    <div
      class="btn btn-icon w-auto d-flex align-items-center btn-lg px-2 kt-topbar-user-trigger"
      id="kt_quick_user_mobi_toggle"
      :class="{ 'btn-clean': !isCustomStyle }"
    >
      <span
        class="font-weight-bolder font-size-base d-none d-md-inline kt-topbar-user-greeting"
        :class="{ 'text-white': isCustomStyle, 'text-dark-50': !isCustomStyle }"
      >
        {{ $t("navbar_user_greeting", { name: currentUser.data.name }) }}
      </span>
      <span
        class="symbol symbol-35 symbol-light-success flex-shrink-0 kt-topbar-user-avatar"
      >
        <img
          v-if="currentUser.data.photo.url"
          :alt="currentUser.data.name"
          :src="currentUser.data.photo.url"
        />
        <span v-else class="symbol-label font-size-h5 font-weight-bold">
          {{ currentUser.data.photo.first_letter.toUpperCase() }}
        </span>
      </span>
    </div>

    <div
      id="kt_quick_user"
      ref="kt_quick_user"
      class="offcanvas offcanvas-right p-10"
    >
      <!--begin::Header-->
      <div
        class="offcanvas-header d-flex align-items-center justify-content-between pt-10"
      >
        <h3 class="font-weight-bold m-0">{{ $t("user_profile") }}</h3>
        <a
          href="#"
          class="btn btn-xs btn-icon btn-light btn-hover-info"
          id="kt_quick_user_mobi_close"
        >
          <v-icon>mdi-close</v-icon>
        </a>
      </div>
      <!--end::Header-->

      <!--begin::Content-->
      <perfect-scrollbar
        class="offcanvas-content pr-5 mr-n5 scroll"
        style="max-height: 90vh; position: relative"
      >
        <!--begin::Header-->
        <div class="d-flex align-items-center mt-5 quick-user-header">
          <div class="symbol symbol-100 mr-5 flex-shrink-0">
            <img
              v-if="currentUser.data.photo.url"
              :alt="currentUser.data.name"
              :src="currentUser.data.photo.url"
            />
            <span v-else class="symbol symbol-35 symbol-light-success">
              <span class="symbol-label font-size-h5 font-weight-bold">
                {{ currentUser.data.photo.first_letter.toUpperCase() }}
              </span>
            </span>
          </div>
          <div class="d-flex flex-column quick-user-info">
            <router-link
              to="/profile"
              class="font-weight-bold font-size-h5 text-dark-75 text-hover-primary"
            >
              {{ currentUser.data.name }}
            </router-link>
            <div class="text-muted mt-1">{{ organization }}</div>
            <div class="navi mt-2">
              <a href="#" class="navi-item">
                <span class="navi-link p-0 pb-2">
                  <span class="navi-text text-muted text-hover-primary">
                    {{ currentUser.data.email }}
                  </span>
                </span>
              </a>
            </div>
            <button class="btn btn-light-info btn-bold" @click="onLogout">
              {{ $t("sign_out") }}
            </button>
          </div>
        </div>
        <!--end::Header-->
        <div class="separator separator-dashed mt-8 mb-5"></div>
        <!--begin::Nav-->
        <div class="navi navi-spacer-x-0 p-0">
          <!--begin::Item-->
          <router-link
            to="/profile"
            @click.native="closeOffcanvas"
            class="navi-item"
          >
            <div class="navi-link">
              <div class="symbol symbol-40 bg-light mr-3 flex-shrink-0">
                <div class="symbol-label">
                  <span class="svg-icon svg-icon-md svg-icon-success">
                    <v-icon>mdi-information-outline</v-icon>
                    <!--end::Svg Icon-->
                  </span>
                </div>
              </div>
              <div class="navi-text text-left">
                <div class="font-weight-bold">{{ $t("my_profile") }}</div>
                <div class="text-muted">
                  {{ $t("account_settings_and_more") }}
                  <span
                    class="label label-light-danger label-inline font-weight-bold"
                  >
                    {{ $t("update") }}
                  </span>
                </div>
              </div>
            </div>
          </router-link>
          <!--end:Item-->
        </div>
        <!--end::Nav-->
      </perfect-scrollbar>
      <!--end::Content-->
    </div>
  </div>
</template>

<style lang="scss" scoped>
.kt-topbar-user-trigger {
  display: inline-flex !important;
  align-items: center;
  flex-wrap: nowrap;
  column-gap: 0.75rem;
  min-width: 0;
  max-width: min(100%, 22rem);
}

[dir="rtl"] .kt-topbar-user-trigger {
  flex-direction: row-reverse;
}

.kt-topbar-user-greeting {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  unicode-bidi: isolate;
}

// Vuetify is configured with `rtl: false` in this project, so `<v-app>`
// always emits `.v-application--is-ltr`. To make the user-profile offcanvas
// behave identically in RTL, we mirror the physical Bootstrap 4 spacing here
// using rules keyed off the document's `dir` attribute, and force the panel's
// `direction` so flex order, text-align, and align-self all agree.
#kt_quick_user {
  overflow: hidden;

  .quick-user-header {
    flex-wrap: nowrap;
  }

  .quick-user-info {
    flex: 1 1 auto;
    min-width: 0;
  }

  .quick-user-info > a,
  .quick-user-info > .text-muted,
  .quick-user-info .navi-text {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .quick-user-info > .btn {
    align-self: flex-start;
  }
}

[dir="rtl"] #kt_quick_user {
  direction: rtl;

  .quick-user-header > .symbol.mr-5 {
    margin-right: 0 !important;
    margin-left: 20px !important;
  }

  .navi-link > .symbol.mr-3 {
    margin-right: 0 !important;
    margin-left: 12px !important;
  }

  .quick-user-info,
  .quick-user-info ::v-deep *,
  ::v-deep .navi-text,
  ::v-deep .navi-text > * {
    text-align: right;
  }
}

[dir="ltr"] #kt_quick_user {
  direction: ltr;

  .quick-user-info,
  .quick-user-info ::v-deep *,
  ::v-deep .navi-text,
  ::v-deep .navi-text > * {
    text-align: left;
  }
}
</style>

<script>
import { mapGetters } from "vuex";
import { LOGOUT } from "@/core/services/store/auth.module";
import KTLayoutQuickUser from "@/assets/js/layout/extended/quick-user-mobi.js";
import KTOffcanvas from "@/assets/js/components/offcanvas.js";

export default {
  name: "KTQuickUserMobi",
  props: ["customStyle"],
  data() {
    return {};
  },
  mounted() {
    // Init Quick User Panel
    KTLayoutQuickUser.init(this.$refs["kt_quick_user"]);
  },
  methods: {
    onLogout() {
      this.$store
        .dispatch(LOGOUT)
        .then(() => this.$router.push({ name: "login" }));
    },
    closeOffcanvas() {
      new KTOffcanvas(KTLayoutQuickUser.getElement()).hide();
    },
  },
  computed: {
    isCustomStyle: function () {
      return this.customStyle ? true : false;
    },
    ...mapGetters(["currentUser"]),
    organization: function () {
      return this.$store.getters.currentUser.data.organization_name;
    },
  },
};
</script>
